import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  Folder,
  FolderOpen,
  MoreHorizontal,
  Pencil,
  Trash2,
  FolderInput,
} from 'lucide-react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { cn } from '@/core/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import type { ProjectFolderNodeDto } from '../types';

interface FolderTreeNodeProps {
  node: ProjectFolderNodeDto;
  activeFolderId: string | null;
  onSelect: (folderId: string) => void;
  onRename: (folderId: string, currentName: string) => void;
  onDelete: (folderId: string) => void;
  onMove: (folderId: string) => void;
}

export function FolderTreeNode({
  node,
  activeFolderId,
  onSelect,
  onRename,
  onDelete,
  onMove,
}: FolderTreeNodeProps) {
  const { t } = useTranslation('projectFiles');
  const [expanded, setExpanded] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const hasChildren = (node.children?.length ?? 0) > 0;
  const isActive = activeFolderId === node.id;
  const paddingLeft = (node.depth + 1) * 12;

  useEffect(() => {
    if (!ref.current || !node.id) return;
    return dropTargetForElements({
      element: ref.current,
      getData: () => ({ folderId: node.id }),
      canDrop: ({ source }) => source.data.type === 'file',
      onDragEnter: () => {
        setIsDraggedOver(true);
        // Auto-expand when dragging a file over a collapsed folder that has children
        if (hasChildren) setExpanded(true);
      },
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: () => setIsDraggedOver(false),
    });
  }, [node.id, hasChildren]);

  return (
    <div>
      <div
        ref={ref}
        className={cn(
          'group/folder flex items-center gap-1 rounded-md py-1 pr-1 cursor-pointer transition-colors',
          isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted/60',
          isDraggedOver && 'ring-2 ring-primary ring-inset bg-primary/10',
        )}
        style={{ paddingLeft }}
        data-testid={`project-files-sidebar-folder-${node.id}`}
        onClick={() => node.id && onSelect(node.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && node.id) onSelect(node.id);
        }}
        aria-selected={isActive}
      >
        <button
          type="button"
          className={cn(
            'size-4 shrink-0 flex items-center justify-center transition-transform',
            !hasChildren && 'invisible',
            expanded && 'rotate-90',
          )}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          aria-expanded={expanded}
          tabIndex={-1}
        >
          <ChevronRight className="size-3" />
        </button>

        {expanded || isDraggedOver ? (
          <FolderOpen className="size-4 shrink-0" />
        ) : (
          <Folder className="size-4 shrink-0" />
        )}

        <span className="min-w-0 flex-1 truncate text-sm">{node.name}</span>

        <div
          className="opacity-0 group-hover/folder:opacity-100 focus-within:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    aria-label={t('folder.actions')}
                  >
                    <MoreHorizontal className="size-3" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t('folder.actions')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => node.id && onRename(node.id, node.name ?? '')}>
                <Pencil className="size-4 mr-2" />
                {t('folder.rename')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => node.id && onMove(node.id)}>
                <FolderInput className="size-4 mr-2" />
                {t('folder.move')}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => node.id && onDelete(node.id)}
              >
                <Trash2 className="size-4 mr-2" />
                {t('folder.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {expanded && hasChildren && (
        <div>
          {(node.children ?? []).map((child) => (
            <FolderTreeNode
              key={child.id}
              node={child}
              activeFolderId={activeFolderId}
              onSelect={onSelect}
              onRename={onRename}
              onDelete={onDelete}
              onMove={onMove}
            />
          ))}
        </div>
      )}
    </div>
  );
}
